/**
 * Supabase 客户端（浏览器端 / Electron 渲染进程用）
 * anon key 是公开的，不算敏感信息。
 *
 * 支持通过 env override 切换 Supabase 项目（dev / prod）：
 *   VITE_SUPABASE_URL        覆盖 project URL
 *   VITE_SUPABASE_ANON_KEY   覆盖 anon key
 *
 * 在 apps/desktop/.env.dev 或 apps/desktop/.env.prod 设置即可，打包后依旧用编译时的值。
 */
import { createClient } from "@supabase/supabase-js";

// Prod Supabase project (qsfmmlajfdsyxpqthtlo), created 2026-04-18 after the
// previous prod ref (mopcxmaqspxohhyuvykt) was deleted during the dual-env
// setup. anon key is a public JWT — it's meant to be shipped to clients, so
// baking it into the Electron bundle is fine; the guardrail below also
// verifies the URL and key point at the same project.
const PROD_URL = "https://qsfmmlajfdsyxpqthtlo.supabase.co";
const PROD_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFzZm1tbGFqZmRzeXhwcXRodGxvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0OTMwNzYsImV4cCI6MjA5MjA2OTA3Nn0.q2JvzWfD8GX84NHFh5kPPp0VdU-gNSLFED1ZP3ONYlA";

const SUPABASE_URL =
  (typeof import.meta !== "undefined" && (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) || PROD_URL;
const SUPABASE_ANON_KEY =
  (typeof import.meta !== "undefined" && (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY) || PROD_ANON_KEY;

// Guardrail: catch the "URL points at project A but ANON_KEY was copied from
// project B" footgun at boot, before auth silently 401s on every request.
// This used to burn hours diagnosing when one of the two was forgotten in
// .env.local after switching between test/prod Supabase projects.
function parseProjectRefFromUrl(url: string): string | null {
  const m = url.match(/https?:\/\/([a-z0-9]+)\.supabase\.co/i);
  return m ? m[1]!.toLowerCase() : null;
}
function parseProjectRefFromAnonKey(anonKey: string): string | null {
  try {
    const parts = anonKey.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? atob(b64 + pad)
        : Buffer.from(b64 + pad, "base64").toString("utf-8");
    const payload = JSON.parse(json) as { ref?: unknown };
    return typeof payload.ref === "string" ? payload.ref.toLowerCase() : null;
  } catch {
    return null;
  }
}
const urlRef = parseProjectRefFromUrl(SUPABASE_URL);
const anonRef = parseProjectRefFromAnonKey(SUPABASE_ANON_KEY);
if (urlRef && anonRef && urlRef !== anonRef) {
  // Throw so nothing downstream keeps running on a broken client — better a
  // loud failure at boot than silent 401s on every API call.
  throw new Error(
    `[supabase] project ref mismatch: URL=${urlRef} but ANON_KEY=${anonRef}. ` +
      `Fix VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY in apps/desktop/.env.* (or unset both to use the baked-in prod defaults).`,
  );
}

// Dev-only durable session storage.
//
// In `bun run dev` the Electron process is hard-killed on every restart, and
// Chromium's localStorage flushes to disk asynchronously — so the latest
// *rotated* refresh token often never reaches disk, the next boot replays a
// consumed token, and Supabase forces a re-login every time ("Invalid Refresh
// Token: Refresh Token Not Found"). Routing the session through the Electron
// main process (synchronous file write in userData) makes it survive hard
// kills. Gated to DEV so prod keeps the default localStorage behavior untouched.
interface AuthStoreBridge {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

function isDevBuild(): boolean {
  if (typeof import.meta === "undefined") return false;
  const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
  return env?.DEV === true;
}

function resolveDurableStorage() {
  if (!isDevBuild() || typeof window === "undefined") return undefined;
  const bridge = (window as unknown as { electronAPI?: { authStore?: AuthStoreBridge } })
    .electronAPI?.authStore;
  if (!bridge) return undefined;
  // Returning the IPC promises (rather than fire-and-forget) makes supabase-js
  // await the durable write before continuing, so the on-disk session is never
  // behind the in-memory one when the process is killed.
  return {
    getItem: (key: string): Promise<string | null> => bridge.get(key),
    setItem: (key: string, value: string): Promise<void> => bridge.set(key, value),
    removeItem: (key: string): Promise<void> => bridge.remove(key),
  };
}

const durableStorage = resolveDurableStorage();

export const supabase = durableStorage
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, { auth: { storage: durableStorage } })
  : createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
